<template>
  <div>
    <!-- Encabezado -->
    <div class="flex items-center justify-between mb-6">
      <button 
        class="mr-2 p-2 text-white bg-teal-800 hover:bg-teal-900 rounded transition-colors" 
        @click="back()"
        title="Volver atrás"
      >
        <i class="fa fa-arrow-left"></i>
      </button>
      <h1 class="text-3xl font-bold">Gestión de Inventario</h1>
    </div>

    <Head title="Gestión de Inventario" />

    <!-- Buscador de productos -->
    <div class="bg-white rounded-lg shadow-md overflow-hidden mb-6">
      <div class="p-6 border-b">
        <h3 class="text-xl font-semibold">Búsqueda de Productos</h3>
        
        <div class="mt-4">
          <text-input 
            v-model="form.search.terms"
            label="Buscar por nombre, código o descripción"
            placeholder="Ingrese términos de búsqueda"
            class="w-full lg:w-1/2"
          />
        </div>
      </div>

      <!-- Tabla de resultados de búsqueda -->
      <div class="overflow-x-auto">
        <table class="w-full whitespace-nowrap">
          <thead>
            <tr class="text-left bg-gray-50 border-b border-gray-200">
              <th class="py-3 px-6 font-semibold">Código</th>
              <th class="py-3 px-6 font-semibold">Producto</th>
              <th class="py-3 px-6 font-semibold text-center">Total General</th>
              <th class="py-3 px-6 font-semibold">Stock por Tienda / Depósito</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="product in products.data" 
              :key="product.id"
              class="hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-100"
              @click="addProduct(product)"
            >
              <td class="py-3 px-6 font-mono font-medium text-gray-700">{{ product?.code }}</td>
              <td class="py-3 px-6 font-bold text-gray-900">{{ product?.name }}</td>
              <td class="py-3 px-6 text-center">
                <span 
                  class="inline-block px-3 py-1 rounded-full text-xs font-black" 
                  :class="(product?.total_stock !== undefined ? product.total_stock : product?.quantity) > 0 ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-700'"
                >
                  {{ (product?.total_stock !== undefined ? product.total_stock : product?.quantity) }}
                </span>
              </td>
              <td class="py-3 px-6">
                <div class="flex flex-wrap gap-1.5 items-center max-w-xl">
                  <template v-if="product?.all_deposits && product.all_deposits.length">
                    <span 
                      v-for="dep in product.all_deposits" 
                      :key="dep.deposit_id"
                      class="inline-flex items-center text-xs px-2.5 py-1 rounded-md border font-semibold"
                      :class="dep.quantity > 0 ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : 'bg-gray-50 text-gray-400 border-gray-200'"
                      :title="`${dep.deposit_name}: ${dep.quantity} en stock`"
                    >
                      <span>{{ dep.deposit_name }}:</span>
                      <strong class="ml-1 text-xs" :class="dep.quantity > 0 ? 'text-emerald-950 font-black' : 'text-gray-400'">{{ dep.quantity }}</strong>
                    </span>
                  </template>
                  <span v-else class="text-xs text-gray-400 italic">Sin detalle por tienda</span>
                </div>
              </td>
            </tr>
            <tr v-if="!products?.data?.length">
              <td colspan="4" class="py-4 px-6 text-center text-gray-500">
                No se encontraron productos
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <pagination 
        v-if="products?.meta?.links" 
        :links="products.meta.links" 
        class="p-4 border-t" 
      />
    </div>

    <!-- Lista de productos seleccionados -->
    <div class="bg-white rounded-lg shadow-md overflow-hidden">
      <div class="p-6 border-b">
        <h3 class="text-xl font-semibold">Productos Seleccionados</h3>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full whitespace-nowrap">
          <thead>
            <tr class="text-left bg-gray-50">
              <th class="py-3 px-6 font-semibold">Cantidad</th>
              <th class="py-3 px-6 font-semibold">Depósito</th>
              <th class="py-3 px-6 font-semibold">Código</th>
              <th class="py-3 px-6 font-semibold">Producto</th>
              <th class="py-3 px-6 font-semibold">Stock Actual</th>
              <th class="py-3 px-6"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="product in items" :key="product.id" class="hover:bg-gray-50">
              <td class="py-3 px-6">
                <text-input
                  v-model="quantities.qtys[product.id]"
                  type="number"
                  min="1"
                  placeholder="Cantidad"
                  class="w-24"
                />
              </td>
              <td class="py-3 px-6">
                <select-input 
                  v-model="quantities.deposits[product.id]"
                  class="w-48"
                >
                  <option value="">Seleccionar depósito</option>
                  <option 
                    v-for="deposit in deposits" 
                    :key="deposit.id"
                    :value="deposit.name"
                  >
                    {{ deposit.name }}
                  </option>
                </select-input>
              </td>
              <td class="py-3 px-6">{{ product.code }}</td>
              <td class="py-3 px-6">{{ product.name }}</td>
              <td class="py-3 px-6">{{ product.quantity }}</td>
              <td class="py-3 px-6">
                <button 
                  @click="removeProduct(product)"
                  class="p-2 text-red-600 hover:text-red-800 transition-colors"
                  title="Eliminar producto"
                >
                  <i class="fa fa-trash"></i>
                </button>
              </td>
            </tr>
            <tr v-if="!items.length">
              <td colspan="6" class="py-4 px-6 text-center text-gray-500">
                No hay productos agregados
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="p-6 bg-gray-50">
        <loading-button 
          class="btn-blue"
          :loading="quantities.processing"
          @click="generate"
        >
          <i class="fa fa-save mr-2"></i>
          Guardar Entrada de Productos
        </loading-button>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import Pagination from '@/Shared/Pagination.vue'
import pickBy from 'lodash/pickBy'
import debounce from 'lodash/debounce'

export default {
  components: {
    Head,
    Link,
    TextInput,
    SelectInput,
    Pagination,
  },
  layout: Layout,
  
  props: {
    filters: Object,
    products: Object,
    deposits: Array,
  },

  data() {
    const searchTerms = typeof this.filters?.search === 'object'
      ? (this.filters?.search?.terms ?? '')
      : (this.filters?.search ?? '')

    return {
      quantities: this.$inertia.form({
        qtys: [],
        products: [],
        deposits: [],
      }),

      form: this.$inertia.form({
        search: {
          terms: searchTerms,
          deposit: this.filters?.search?.deposit ?? '',
          brand_id: this.filters?.search?.brand_id ?? '',
          part_id: this.filters?.search?.part_id ?? '',
          truck_model_id: this.filters?.search?.truck_model_id ?? '',
          region_id: this.filters?.search?.region_id ?? '',
        },
      }),

      items: []
    }
  },

  watch: {
    'form.search': {
      handler: debounce(function() {
        this.searchProducts()
      }, 300),
      deep: true
    }
  },

  methods: {
    back() {
      window.history.back()
    },

    searchProducts() {
      this.$inertia.get('/inventories/create', 
        pickBy({ search: this.form.search }), 
        {
          preserveState: true,
          preserveScroll: true,
          replace: true
        }
      )
    },

    addProduct(product) {
      if (!this.items.find(item => item.id === product.id)) {
        this.items.push(product)
        this.$nextTick(() => {
          this.quantities.qtys[product.id] = 1
        })
      }
    },

    removeProduct(product) {
      if (confirm('¿Está seguro que desea eliminar este producto?')) {
        this.items = this.items.filter(item => item.id !== product.id)
        delete this.quantities.qtys[product.id]
        delete this.quantities.deposits[product.id]
      }
    },

    generate() {
      if (!this.items.length) {
        alert('Debe agregar al menos un producto')
        return
      }

      this.quantities.products = this.items.map(item => item.id)
      this.quantities.post('/inventories')
    }
  }
}
</script>
